import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as commonServices from "../../Services/common";

interface MedicineItem {
  product_detail_id: number;
  medicine: number;
  batch_no: string;
  qty: number;
  unit_price: number;
  expiry_date: Date | string;
  cgst: number;
  sgst: number;
  gstType: string;
  total: number;
  created_at?: string;
  order_product_id?: number; // For updating existing orders
}

interface ServiceItem {
  service: number;
  qty: number;
  unit_price: number;
  cgst: number;
  sgst: number;
  total: number;
  created_at?: string;
  order_service_id?: number; // For updating existing orders
}

interface MedicineFormData {
  medicine: string;
  batch_no: string;
  qty: string;
  unit_price: string;
  expiry_date: Date | null;
  totalQty: number;
  product_detail_id: number;
  cgst: number;
  sgst: number;
  gstType: string;
  allData: any[];
}

interface ServiceFormData {
  service: string;
  servicePrice: string;
  serviceQty: string;
  serviceDesc: string;
}

interface CalculationsData {
  discountType: string;
  discountValue: string;
  paymentMode: string;
  amountReceived: string;
  comments: string;
  hospitalUnit: string;
  totalUnits: string;
  total: string;
  tax: string;
  subTotal: string;
  discount: string;
  roundOff: string;
  grandTotal: string;
  amountPaid: string;
  dueAmount: string;
  fee: string;
}

interface BillingState {
  medicineForm: MedicineFormData;
  serviceForm: ServiceFormData;
  calculations: CalculationsData;
  medicineItems: MedicineItem[];
  serviceItems: ServiceItem[];
  visitId: string | null;
  patientId: string | null;
  billId: string | null;
  billNoInc: string | null;
  ipdDetailsData?: any;
  isIPD: boolean;
  payments: any[];
  isCancelled: boolean;
}

const initialState: BillingState = {
  medicineForm: {
    medicine: "",
    batch_no: "",
    qty: "",
    unit_price: "",
    expiry_date: null,
    totalQty: 0,
    product_detail_id: 0,
    cgst: 0,
    sgst: 0,
    gstType: "",
    allData: [],
  },
  serviceForm: {
    service: "",
    servicePrice: "",
    serviceQty: "",
    serviceDesc: "",
  },
  calculations: {
    discountType: "FLAT",
    discountValue: "0",
    paymentMode: "Cash",
    amountReceived: "0",
    comments: "",
    hospitalUnit: "",
    totalUnits: "0",
    total: "0.00",
    tax: "0.00",
    subTotal: "0.00",
    discount: "0.00",
    roundOff: "0.00",
    grandTotal: "0.00",
    amountPaid: "0.00",
    dueAmount: "0.00",
    fee: "0",
  },
  medicineItems: [],
  serviceItems: [],
  visitId: null,
  patientId: null,
  billId: null,
  billNoInc: null,
  isIPD: false,
  payments: [],
  isCancelled: false,
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    // Medicine form actions
    updateMedicineForm: (state, action: PayloadAction<Partial<MedicineFormData>>) => {
      state.medicineForm = { ...state.medicineForm, ...action.payload };
    },
    clearMedicineForm: (state) => {
      state.medicineForm = {
        medicine: "",
        batch_no: "",
        qty: "",
        unit_price: "",
        expiry_date: null,
        totalQty: 0,
        product_detail_id: 0,
        cgst: 0,
        sgst: 0,
        gstType: "",
        allData: state.medicineForm.allData, // Keep allData
      };
    },

    // Service form actions
    updateServiceForm: (state, action: PayloadAction<Partial<ServiceFormData>>) => {
      state.serviceForm = { ...state.serviceForm, ...action.payload };
    },
    clearServiceForm: (state) => {
      state.serviceForm = {
        service: "",
        servicePrice: "",
        serviceQty: "",
        serviceDesc: "",
      };
    },

    // Add items
    addMedicineItem: (state, action: PayloadAction<MedicineItem>) => {
      state.medicineItems.push(action.payload);
      // Reset discount when items are added
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    addServiceItem: (state, action: PayloadAction<ServiceItem>) => {
      state.serviceItems.push(action.payload);
      // Reset discount when items are added
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Update items
    updateMedicineItem: (state, action: PayloadAction<{ index: number; qty: number }>) => {
      const item = state.medicineItems[action.payload.index];
      if (item) {
        item.qty = action.payload.qty;
        item.total = item.qty * item.unit_price;
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },
    updateServiceItem: (state, action: PayloadAction<{ index: number; qty: number }>) => {
      const item = state.serviceItems[action.payload.index];
      if (item) {
        item.qty = action.payload.qty;
        item.total = item.qty * item.unit_price;
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },

    // Remove items
    removeMedicineItem: (state, action: PayloadAction<number>) => {
      state.medicineItems.splice(action.payload, 1);
      // Reset discount when items are deleted
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    removeServiceItem: (state, action: PayloadAction<number>) => {
      state.serviceItems.splice(action.payload, 1);
      // Reset discount when items are deleted
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Calculations actions
    updateCalculations: (state, action: PayloadAction<Partial<CalculationsData>>) => {
      state.calculations = { ...state.calculations, ...action.payload };
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Recalculate amounts
    recalculateAmounts: (state) => {
      const data = commonServices.getAmountCalculatedForMedicineOrder({
        arr: state.medicineItems,
        serviceArr: state.serviceItems,
        discountType: state.calculations.discountType,
        discountValue: parseFloat(state.calculations.discountValue) || 0,
        fee: parseFloat(state.calculations.fee) || 0,
        isIpd: state.isIPD,
        ipdDetailsData: state.ipdDetailsData,
      });

      state.calculations.totalUnits = data.totalUnits?.toString() || "0";
      state.calculations.total = data.total?.toFixed(2) || "0.00";
      state.calculations.tax = data.tax?.toFixed(2) || "0.00";
      state.calculations.subTotal = data.subTotal?.toFixed(2) || "0.00";
      state.calculations.discount = data.discount?.toFixed(2) || "0.00";

      // Calculate round off: difference between rounded and actual grand total
      // roundOff = rounded_value - actual_value
      // If grandTotal is 100.48, roundOff = 100 - 100.48 = -0.48
      // If grandTotal is 99.52, roundOff = 100 - 99.52 = 0.48
      const actualGrandTotal = data.grandTotal || 0;
      const roundedGrandTotal = Math.round(actualGrandTotal);
      const roundOff = roundedGrandTotal - actualGrandTotal;
      state.calculations.roundOff = roundOff.toFixed(2);

      // Grand Total should be the rounded value (which automatically accounts for round off)
      // This ensures: Grand Total = Sub Total - Discount - Round Off
      state.calculations.grandTotal = roundedGrandTotal.toFixed(2);

      // Calculate due amount based on the final grand total (after round off)
      // Due Amount = Grand Total (after round off) - (Amount Paid + Amount Received)
      // Amount Paid = total from previous payments, Amount Received = new payment being entered
      const finalGrandTotal = parseFloat(state.calculations.grandTotal) || 0;
      const amountPaid = parseFloat(state.calculations.amountPaid || "0") || 0;
      const amountReceived = parseFloat(state.calculations.amountReceived || "0") || 0;
      const dueAmount = finalGrandTotal - (amountPaid + amountReceived);
      state.calculations.dueAmount = dueAmount.toFixed(2);
    },

    // Set billing context
    setBillingContext: (
      state,
      action: PayloadAction<{
        visitId: string;
        patientId: string;
        billId?: string;
        billNoInc?: string;
        isIPD?: boolean;
        ipdDetailsData?: any;
        clearItems?: boolean;
      }>,
    ) => {
      // Clear items if visitId or patientId changes (switching between patients)
      const visitIdChanged = state.visitId !== action.payload.visitId;
      const patientIdChanged = state.patientId !== action.payload.patientId;

      if (action.payload.clearItems || visitIdChanged || patientIdChanged) {
        state.medicineItems = [];
        state.serviceItems = [];
        state.payments = [];
        // Reset payment-related calculations when switching patients
        state.calculations.amountPaid = "0.00";
        state.calculations.dueAmount = "0.00";
        state.calculations.amountReceived = "0";
        // Clear hospital unit when switching patients to show selection for new patient
        state.calculations.hospitalUnit = "";
        // Clear invoice number when switching patients - will be regenerated based on selected unit
        state.billNoInc = null;
        // Reset cancelled status when switching patients
        state.isCancelled = false;
      }

      state.visitId = action.payload.visitId;
      state.patientId = action.payload.patientId;
      state.billId = action.payload.billId || null;
      state.billNoInc = action.payload.billNoInc || null;
      state.isIPD = action.payload.isIPD || false;
      state.ipdDetailsData = action.payload.ipdDetailsData;
    },

    // Clear all billing data
    clearBillingData: (state) => {
      return { ...initialState };
    },

    // Load billing data from API
    loadBillingData: (
      state,
      action: PayloadAction<{
        medicineItems?: MedicineItem[];
        serviceItems?: ServiceItem[];
        calculations?: Partial<CalculationsData>;
        billNoInc?: string;
        billId?: string;
        payments?: any[];
        clearItems?: boolean; // Flag to explicitly clear items
        isCancelled?: boolean; // Flag to indicate if bill is cancelled
      }>,
    ) => {
      // Clear or update items if explicitly requested or if new items are being loaded
      const shouldUpdateMedicineItems =
        action.payload.clearItems || action.payload.medicineItems !== undefined;
      const shouldUpdateServiceItems =
        action.payload.clearItems || action.payload.serviceItems !== undefined;

      if (shouldUpdateMedicineItems) {
        state.medicineItems = action.payload.medicineItems || [];
      }
      if (shouldUpdateServiceItems) {
        state.serviceItems = action.payload.serviceItems || [];
      }

      // If clearing items, always reset payment-related data
      if (action.payload.clearItems) {
        state.payments = [];
        state.calculations.amountPaid = "0.00";
        state.calculations.dueAmount = "0.00";
        state.calculations.amountReceived = "0";
        // Clear hospital unit when clearing items (new patient with no saved order)
        state.calculations.hospitalUnit = "";
        // Clear invoice number - will be regenerated based on selected unit when saving
        state.billNoInc = null;
        // Reset cancelled status when clearing items
        state.isCancelled = false;
      }

      // Update cancelled status if provided
      if (action.payload.isCancelled !== undefined) {
        state.isCancelled = action.payload.isCancelled;
      }

      if (action.payload.calculations) {
        state.calculations = { ...state.calculations, ...action.payload.calculations };
      }
      if (action.payload.billNoInc !== undefined) {
        state.billNoInc = action.payload.billNoInc;
      }
      if (action.payload.billId !== undefined) {
        state.billId = action.payload.billId;
      }
      // Only update payments if clearItems is false (to avoid overwriting the clear above)
      if (action.payload.payments !== undefined && !action.payload.clearItems) {
        state.payments = action.payload.payments || [];
      }
      // Recalculate amounts after loading data
      billingSlice.caseReducers.recalculateAmounts(state);
    },
  },
});

export const {
  updateMedicineForm,
  clearMedicineForm,
  updateServiceForm,
  clearServiceForm,
  addMedicineItem,
  addServiceItem,
  updateMedicineItem,
  updateServiceItem,
  removeMedicineItem,
  removeServiceItem,
  updateCalculations,
  recalculateAmounts,
  setBillingContext,
  clearBillingData,
  loadBillingData,
} = billingSlice.actions;

export default billingSlice.reducer;
